import { CARD } from '@/components/common/surfaces';
import { ModelPreview } from '@/components/project-detail/ModelPreview';
import type { ModelSolid } from '@/types/project.types';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ModelColumnProps {
  title: string;
  /** Id of the project's one STEP document — the same for both solids. */
  modelId: string;
  /** Which solid of that document this column shows. */
  solid?: ModelSolid;
  /**
   * Whether the document actually carries a preview for this solid. A STEP without a stock
   * solid still produces a document, so the stock column has to say "no preview" rather than
   * request one that would 404.
   */
  hasPreview: boolean;
}

/**
 * One preview card of the project's STEP document.
 *
 * Uploading is no longer per column: the model and the stock come out of a single uploaded STEP,
 * so the upload zone lives once on the page rather than once per solid.
 */
export const ModelColumn: FC<ModelColumnProps> = ({ title, modelId, solid = 'model', hasPreview }) => {
  const { t } = useTranslation();

  return (
    <div className={`${CARD} flex flex-col gap-4 p-4`}>
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>

      <div className="aspect-video overflow-hidden rounded-xl bg-gray-50">
        {hasPreview ? (
          <ModelPreview modelId={modelId} solid={solid} alt={`${title} preview`} />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <p className="m-0 text-sm text-gray-500">{t('models.noPreview')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
